import React from 'react';
import { NavLink } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import AnimatedSection from './common/AnimatedSection';

import img1 from '../assets/images/services/interior/interior-design.jpg';
import img2 from '../assets/images/edges-design3.jpg';
import img3 from '../assets/images/projects/muya-apartment/1.jpg';

const highlights = [
  'Architecture & structural design',
  'Construction management & oversight',
  'Interior design & finishing',
  'Surveying & real estate advisory',
];

function About() {
  return (
    <AnimatedSection
      id="about"
      className="bg-white py-24 px-6 md:px-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16">

        {/* ── IMAGE STACK ── */}
        <div className="relative flex-shrink-0 w-full lg:w-[48%] h-[480px] md:h-[540px]">

          {/* Main large image */}
          <div className="absolute top-0 left-0 w-[72%] h-[85%] rounded-2xl overflow-hidden shadow-xl">
            <img src={img1} alt="Interior design" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>

          {/* Second image — bottom-right, overlapping */}
          <div className="absolute bottom-0 right-0 w-[55%] h-[55%] rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
            <img src={img2} alt="Architecture" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>

          {/* Third small image — middle left edge */}
          <div className="absolute top-[38%] left-[-6%] w-[36%] h-[30%] rounded-xl overflow-hidden shadow-xl border-4 border-white hidden md:block">
            <img src={img3} alt="Project" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>

          {/* Floating stat badge */}
          <div className="absolute bottom-[20%] left-[30%] bg-teal-600 text-white px-5 py-3 rounded-xl shadow-lg z-10 text-center">
            <p className="text-2xl font-extrabold leading-none">50+</p>
            <p className="text-xs font-medium mt-0.5 opacity-90 tracking-wide">Projects Done</p>
          </div>

          {/* Teal accent block */}
          <div className="absolute top-[-16px] left-[-16px] w-24 h-24 bg-teal-50 rounded-2xl -z-10" />
          <div className="absolute bottom-[-16px] right-[-16px] w-20 h-20 bg-teal-50 rounded-2xl -z-10" />
        </div>

        {/* ── TEXT + CTA ── */}
        <div className="flex-1 max-w-xl">

          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-5">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-teal-500 shadow-[0_0_8px_3px_rgba(20,184,166,0.7)]" />
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_6px_2px_rgba(20,184,166,0.5)]" />
              <span className="w-1 h-1 rounded-full bg-teal-300 shadow-[0_0_4px_1px_rgba(20,184,166,0.3)]" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600">
              Who We Are
            </span>
          </div>

          {/* Heading */}
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-snug">
            Building Tomorrow,{' '}
            <span className="text-teal-600">Today</span>
          </h2>

          {/* Body */}
          <p className="mt-5 text-gray-500 text-sm md:text-base leading-relaxed">
            At <span className="font-semibold text-gray-700">Edges Design Group</span>, we
            specialize in bringing visionary architectural and construction concepts to life.
            Our team of skilled architects, engineers, and builders merges innovation with
            practicality to deliver spaces that inspire and endure — from blueprint to build.
          </p>

          {/* Highlights */}
          <ul className="mt-7 space-y-3">
            {highlights.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-gray-600">
                <CheckCircle size={16} className="text-teal-500 shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          {/* Divider */}
          <div className="my-8 h-px bg-gray-100" />

          {/* CTA block */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <NavLink
              to="/about"
              className="inline-flex items-center gap-2 px-7 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-sm hover:-translate-y-0.5"
            >
              Learn More About Us <ArrowRight size={15} />
            </NavLink>
            <NavLink
              to="/contacts"
              className="inline-flex items-center gap-2 px-7 py-3 border border-gray-200 hover:border-teal-500 text-gray-600 hover:text-teal-600 text-sm font-medium rounded-lg transition-all duration-200 hover:-translate-y-0.5"
            >
              Start a Project
            </NavLink>
          </div>

          {/* Trust line */}
          <p className="mt-5 text-xs text-gray-400 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-teal-400" />
            Trusted by homeowners and developers across Rwanda & East Africa
          </p>
        </div>

      </div>
    </AnimatedSection>
  );
}

export default About;
